import React, { useContext, useMemo, useState } from "react";
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { BoxFull } from "iuvity-ui-components/dist/boxfull";
import { TableHeader } from "../datatable/TableHeader";
import { FilterMatchMode } from "primereact/api";
import { Column } from "iuvity-ui-components/dist/column";
import { FormSkeleton } from "../skeleton/FormSkeleton";
import { Actions } from "../datatable/Actions";
import { DialogProps } from "../dialog/CustomDialog";
import { useCampaigns } from "../../hooks/useCampaigns";
import { MarketingCampaign } from "../../interfaces";
import { AppContext } from "../../context/index";
import { useLocation, useNavigate } from "react-router-dom";
import { allFieldsBodyTemplate } from "../../components/datatable/allFieldsBodyTemplate";
import { subheaderActionsPrivilegeValidator } from "../../helpers/subheaderActionsPrivilegeValidator";

interface IMDataTableActionsProps {
  auxModalController: React.Dispatch<React.SetStateAction<DialogProps>>;
}

export const IMDataTableActions: React.FC<IMDataTableActionsProps> = ({
  auxModalController,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { 
    appState: { permissiondInfoByRole },
    t,
    setAppState } = useContext(AppContext);

  const { data, campaignAction, reloadFn } = useCampaigns("DATA");

  const columns = useMemo(() => {
    return [
      {
        field: "advertiseid",
        header: t("IuviMarketing.advertiseidLabel"),
      },
      {
        field: "type",
        header: t("IuviMarketing.typeLabel"),
      },
      {
        field: "status",
        header: t("IuviMarketing.statusLabel"),
      },
      {
        field: "actions",
        header: t("IuviMarketing.actionsLabel"),
      },
    ];
  }, [t]);

  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const actionEditFn = (campaign?: MarketingCampaign) => {
    if (campaign) {
      setAppState((prev) => ({
        ...prev,
        selectedCampaign: campaign,
      }));
      navigate("/configurations/iuvimarketing/edit");
    }
  };

  const actionDeleteFn = async (campaign: any) => {
    await campaignAction("DELETE", campaign.id);
    reloadFn();
  };

  const handleModalProps = (campaign: any) => {
    auxModalController((prev) => ({
      ...prev,
      visible: true,
      title: t("IuviMarketing.deleteModalTitle"),
      content: `${t("IuviMarketing.deleteModalContent1")} "${
        campaign.advertiseid
      }" ${t("IuviMarketing.deleteModalContent2")}`,
      onConfirm: () => actionDeleteFn(campaign),
    }));
  };

  return (
    <div className="section_edit_app_env">
      <SubheaderActions
        className="px-3 mx-2 pt-3"
        actions={subheaderActionsPrivilegeValidator([
          {
            id: "111",
            icon: "iu iu-icon-plus",
            label: t("IuviMarketing.addCampaignLabel"),
            style: {
              display: !permissiondInfoByRole.CREATE ? 'none' : undefined,
            },
            onClick: () => navigate(`${location.pathname}/new`),
          },
        ])}
      />

      <BoxFull className="dt-c mt-0 p-3">
        {data ? (
          <DataTable
            header={
              <TableHeader
                title={t("IuviMarketing.marketingCampaingsLabel")}
                columns={columns}
                selectedColumns={selectedColumns}
                toggleColumns={setSelectedColumns}
                filters={filters}
                setFilters={setFilters}
                reload={reloadFn}
              />
            }
            currentPageReportTemplate={t(
              "ConsoleTranslations.rowsPaginatorTemplateLabel"
            )}
            paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
            sortField="order"
            sortOrder={1}
            emptyMessage={t("TableActions.emptyMessage")}
            rows={10}
            size="normal"
            filters={filters}
            value={data.map((campaign) => ({
              ...campaign,
              actions: (
                <Actions
                  enableDelete={!!campaign.createdby}
                  onEdit={() => actionEditFn(campaign)}
                  onDelete={() => handleModalProps(campaign)}
                  visibleObject={{...permissiondInfoByRole}}
                />
              ),
            }))}
            scrollable
            resizableColumns
          >
            {selectedColumns.map((col, idx) => {
              return (
                <Column
                  key={`${col.field}-${idx}`}
                  field={col.field}
                  header={col.header}
                  sortable={!(col.field === "actions")}
                  align={col.field === "actions" ? "center" : "left"}
                  headerStyle={col.field === "actions"
                    ? {
                        justifyContent: "center",
                        display: (!permissiondInfoByRole.UPDATE && !permissiondInfoByRole.REMOVE) ? 'none' : undefined,
                      }
                    : undefined }
                  style={{
                    boxShadow: (col.field === "actions")
                      ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                      : undefined,
                    maxWidth: (col.field === "actions") ? "132px" : undefined,
                    whiteSpace: "normal",
                    display: (col.field === "actions")
                      ? ((!permissiondInfoByRole.UPDATE && !permissiondInfoByRole.REMOVE) ? 'none' : undefined)
                      : undefined,
                  }}
                  frozen={col.field === "actions" ? true : false}
                  alignFrozen={col.field === "actions" ? "right" : undefined}
                  body={(rowData) => allFieldsBodyTemplate(rowData, col.field, 'createdby')}
                />
              );
            })}
          </DataTable>
        ) : (
          <FormSkeleton length={3} />
        )}
      </BoxFull>
    </div>
  );
};
